import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { CalendarDays, Download, FileText, FileSpreadsheet, ReceiptText } from 'lucide-react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Header } from '../components/layout/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000'}/api`;

const currentMonthValue = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const formatCurrency = (value) =>
  new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

function SummaryCard({ label, value, helper }) {
  return (
    <div className="rounded-sm border border-white/10 bg-white/[0.025] p-4">
      <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-zinc-500">{label}</p>
      <p className="mt-3 text-2xl font-semibold text-white">{value}</p>
      {helper ? <p className="mt-1 text-xs text-zinc-500">{helper}</p> : null}
    </div>
  );
}

export default function MonthlyDailyCashierReport() {
  const { user } = useAuth();
  const isAdmin = String(user?.role || '').trim().toLowerCase() === 'admin';
  const [reportMonth, setReportMonth] = useState(currentMonthValue());
  const [merchants, setMerchants] = useState([]);
  const [selectedMerchantId, setSelectedMerchantId] = useState('');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const appendReportParams = (params) => {
    if (reportMonth) params.set('month', reportMonth);
    if (isAdmin && selectedMerchantId) params.set('merchant_id', selectedMerchantId);
    return params;
  };

  const fetchMerchants = async () => {
    if (!isAdmin) return;
    try {
      const { data } = await axios.get(`${API}/merchants`, { withCredentials: true });
      setMerchants(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to fetch merchants:', error);
      setMerchants([]);
    }
  };

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = appendReportParams(new URLSearchParams());
      const { data } = await axios.get(`${API}/reports/daily-cashier/monthly?${params.toString()}`, {
        withCredentials: true,
      });
      setReport(data);
    } catch (error) {
      console.error('Failed to fetch monthly cashier report:', error);
      alert(error.response?.data?.detail || 'Gagal mengambil laporan bulanan kasir.');
    } finally {
      setLoading(false);
    }
  };

  const downloadExcel = async () => {
    setDownloading(true);
    try {
      const params = appendReportParams(new URLSearchParams());
      const { data, headers } = await axios.get(`${API}/reports/daily-cashier/export-monthly?${params.toString()}`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const blobUrl = window.URL.createObjectURL(new Blob([data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      }));
      const contentDisposition = headers['content-disposition'] || '';
      const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
      const filename = filenameMatch?.[1] || `laporan-bulanan-kasir-${reportMonth || 'bulan-ini'}.xlsx`;
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Failed to download monthly cashier excel:', error);
      alert(error.response?.data?.detail || 'Gagal download Excel laporan bulanan kasir.');
    } finally {
      setDownloading(false);
    }
  };

  const downloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const params = appendReportParams(new URLSearchParams());
      const { data, headers } = await axios.get(`${API}/reports/daily-cashier/export-monthly-pdf?${params.toString()}`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const blobUrl = window.URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
      const contentDisposition = headers['content-disposition'] || '';
      const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
      const filename = filenameMatch?.[1] || `rekap-laporan-bulanan-${reportMonth || 'bulan-ini'}.pdf`;
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Failed to download monthly cashier PDF:', error);
      alert(error.response?.data?.detail || 'Gagal download PDF rekap bulanan kasir.');
    } finally {
      setDownloadingPdf(false);
    }
  };

  useEffect(() => {
    fetchMerchants();
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const summary = report?.summary || {};
  const rows = useMemo(() => (Array.isArray(report?.rows) ? report.rows : []), [report]);
  const recaps = useMemo(() => (Array.isArray(report?.recaps) ? report.recaps : []), [report]);
  const coachStatusRecaps = useMemo(() => (Array.isArray(report?.coach_status_recaps) ? report.coach_status_recaps : []), [report]);

  return (
    <DashboardLayout>
      <Header title="Laporan Bulanan Kasir" />
      <div className="space-y-6 p-8" data-testid="monthly-daily-cashier-report-page">
        <section className="rounded-sm border border-white/10 bg-[linear-gradient(135deg,rgba(212,175,55,0.10),rgba(9,9,11,0.94))] p-6">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-[#D4AF37]/10 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.18em] text-[#F3D77A]">
                <FileSpreadsheet className="h-3.5 w-3.5" />
                Monthly Export
              </div>
              <h2 className="text-2xl font-semibold text-white">Download Data Bulanan</h2>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-zinc-500">
                Tarik transaksi kasir per bulan dari sumber yang sama seperti laporan harian, dengan format rekap produk dan pembayaran yang sama.
              </p>
            </div>
            <div className={`grid gap-3 ${isAdmin ? 'sm:grid-cols-[220px_240px_auto_auto_auto]' : 'sm:grid-cols-[220px_auto_auto_auto]'}`}>
              <div>
                <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">Bulan</p>
                <div className="relative">
                  <CalendarDays className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
                  <Input
                    type="month"
                    value={reportMonth}
                    onChange={(event) => setReportMonth(event.target.value)}
                    className="h-11 rounded-sm border-white/10 bg-black/30 pl-10 text-white"
                  />
                </div>
              </div>
              {isAdmin ? (
                <div>
                  <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">Merchant</p>
                  <select
                    value={selectedMerchantId}
                    onChange={(event) => setSelectedMerchantId(event.target.value)}
                    className="h-11 w-full rounded-sm border border-white/10 bg-black/30 px-3 text-sm font-semibold text-white outline-none focus:border-[#D4AF37]/60 [&_option]:bg-white [&_option]:text-black"
                  >
                    <option value="">Semua Merchant</option>
                    {merchants.map((merchant) => (
                      <option key={merchant.id} value={merchant.id}>
                        {merchant.name}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}
              <Button
                type="button"
                onClick={fetchReport}
                disabled={loading || !reportMonth}
                className="self-end rounded-sm border border-white/10 bg-white/[0.04] text-white hover:bg-white/[0.08]"
              >
                {loading ? 'Memuat...' : 'Tampilkan'}
              </Button>
              <Button
                type="button"
                onClick={downloadExcel}
                disabled={loading || downloading || downloadingPdf || !reportMonth}
                className="self-end rounded-sm bg-[#D4AF37] font-semibold text-black hover:bg-[#C59A27]"
              >
                <Download className="mr-2 h-4 w-4" />
                {downloading ? 'Menyiapkan...' : 'Download Excel'}
              </Button>
              <Button
                type="button"
                onClick={downloadPdf}
                disabled={loading || downloading || downloadingPdf || !reportMonth}
                className="self-end rounded-sm border border-[#D4AF37]/40 bg-[#D4AF37]/10 font-semibold text-[#F3D77A] hover:bg-[#D4AF37]/20"
              >
                <FileText className="mr-2 h-4 w-4" />
                {downloadingPdf ? 'Menyiapkan...' : 'Download PDF Rekap'}
              </Button>
            </div>
          </div>
        </section>

        <section className="grid gap-4 md:grid-cols-4">
          <SummaryCard label="Total Baris" value={summary.total_rows || 0} helper="Gabungan payment dan order produk" />
          <SummaryCard label="Total Nominal" value={formatCurrency(summary.total_amount)} helper="Sesuai transaksi paid bulan ini" />
          <SummaryCard label="Paket / Produk" value={recaps.length || 0} helper={`Total daftar/qty: ${summary.total_quantity || 0}`} />
          <SummaryCard label="Master Coach" value={`${summary.total_done_classes || 0} / ${summary.total_not_done_classes || 0}`} helper="Done / Not Done kelas" />
        </section>

        <section className="overflow-hidden rounded-sm border border-white/10 bg-zinc-950/45">
          <div className="border-b border-white/10 px-5 py-4">
            <h3 className="text-base font-semibold text-white">Rekap Paket / Produk</h3>
            <p className="mt-1 text-xs text-zinc-500">Data ini yang masuk ke PDF rekap bulanan: total daftar/qty dan total harga per paket atau produk.</p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-[720px] w-full text-left text-sm">
              <thead className="bg-white/[0.04] text-zinc-300">
                <tr>
                  <th className="px-4 py-3">No</th>
                  <th className="px-4 py-3">Paket / Produk</th>
                  <th className="px-4 py-3 text-right">Total Daftar/Qty</th>
                  <th className="px-4 py-3 text-right">Total Harga</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-7 text-center text-zinc-500">Memuat rekap...</td>
                  </tr>
                ) : recaps.length > 0 ? (
                  recaps.slice(0, 20).map((item, index) => (
                    <tr key={`${item.product}-${index}`} className="border-b border-white/10 text-zinc-200">
                      <td className="px-4 py-3">{index + 1}</td>
                      <td className="px-4 py-3 font-medium text-white">{item.product || '-'}</td>
                      <td className="px-4 py-3 text-right">{Number(item.quantity || 0).toLocaleString('id-ID')}</td>
                      <td className="px-4 py-3 text-right">{formatCurrency(item.total_amount)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="px-4 py-7 text-center text-zinc-500">Belum ada rekap pada bulan ini.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        <section className="overflow-hidden rounded-sm border border-white/10 bg-zinc-950/45">
          <div className="border-b border-white/10 px-5 py-4">
            <h3 className="text-base font-semibold text-white">Rekap Status Master Coach</h3>
            <p className="mt-1 text-xs text-zinc-500">Data ini ikut masuk ke rekap bulanan: status kelas yang sudah ditandai Done atau Not Done.</p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-[820px] w-full text-left text-sm">
              <thead className="bg-white/[0.04] text-zinc-300">
                <tr>
                  <th className="px-4 py-3">No</th>
                  <th className="px-4 py-3">Coach</th>
                  <th className="px-4 py-3">Kelas</th>
                  <th className="px-4 py-3">Tanggal</th>
                  <th className="px-4 py-3 text-right">Done</th>
                  <th className="px-4 py-3 text-right">Not Done</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-7 text-center text-zinc-500">Memuat status kelas...</td>
                  </tr>
                ) : coachStatusRecaps.length > 0 ? (
                  coachStatusRecaps.slice(0, 20).map((item, index) => (
                    <tr key={`${item.coach_name}-${item.class_name}-${item.date}-${index}`} className="border-b border-white/10 text-zinc-200">
                      <td className="px-4 py-3">{index + 1}</td>
                      <td className="px-4 py-3 font-medium text-white">{item.coach_name || '-'}</td>
                      <td className="px-4 py-3">{item.class_name || '-'}</td>
                      <td className="px-4 py-3">{item.date || '-'}</td>
                      <td className="px-4 py-3 text-right text-emerald-300">{Number(item.done_count || 0).toLocaleString('id-ID')}</td>
                      <td className="px-4 py-3 text-right text-rose-300">{Number(item.not_done_count || 0).toLocaleString('id-ID')}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="px-4 py-7 text-center text-zinc-500">Belum ada kelas Master Coach yang ditandai Done/Not Done pada bulan ini.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        <section className="overflow-hidden rounded-sm border border-white/10 bg-zinc-950/45">
          <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
            <div>
              <h3 className="text-base font-semibold text-white">Preview Data</h3>
              <p className="mt-1 text-xs text-zinc-500">Excel bulanan memakai format tabel yang sama seperti laporan harian.</p>
            </div>
            <ReceiptText className="h-5 w-5 text-[#D4AF37]" />
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-[1040px] w-full text-left text-sm">
              <thead className="bg-[#D4AF37] text-black">
                <tr>
                  {['No', 'Transaction', 'Date', 'Member / Customer', 'Product', 'Amount', 'Payment Ref', 'Payment Type'].map((header) => (
                    <th key={header} className="border border-black/40 px-3 py-2 font-bold">{header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-zinc-500">Memuat laporan...</td>
                  </tr>
                ) : rows.length > 0 ? (
                  rows.slice(0, 30).map((item, index) => (
                    <tr key={`${item.transaction}-${index}`} className="border-b border-white/10 text-zinc-200">
                      <td className="px-3 py-2 text-center">{index + 1}</td>
                      <td className="px-3 py-2">{item.transaction || '-'}</td>
                      <td className="px-3 py-2">{item.date || '-'}</td>
                      <td className="px-3 py-2">{item.customer || ''}</td>
                      <td className="px-3 py-2">{item.product || '-'}</td>
                      <td className="px-3 py-2 text-right">{Number(item.amount || 0).toLocaleString('id-ID')}</td>
                      <td className="px-3 py-2">{item.payment_ref || ''}</td>
                      <td className="px-3 py-2">{item.payment_type || '-'}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-zinc-500">Belum ada transaksi pada bulan ini.</td>
                  </tr>
                )}
              </tbody>
              {rows.length > 0 ? (
                <tfoot className="bg-[#D4AF37] font-bold text-black">
                  <tr>
                    <td className="border border-black/40 px-3 py-2" colSpan={4}></td>
                    <td className="border border-black/40 px-3 py-2 text-center">TOTAL</td>
                    <td className="border border-black/40 px-3 py-2 text-right">{Number(summary.total_amount || 0).toLocaleString('id-ID')}</td>
                    <td className="border border-black/40 px-3 py-2" colSpan={2}></td>
                  </tr>
                </tfoot>
              ) : null}
            </table>
          </div>
        </section>
      </div>
    </DashboardLayout>
  );
}
